/**
 * Table Sorting Utilities
 *
 * Shared sorting functions for DataTable components.
 * Used by both client and bcc apps.
 */

/**
 * Sort function for rows with a createdAt date field
 *
 * @example
 * columns: [
 *   {
 *     accessorKey: 'createdAt',
 *     sortingFn: sortByCreatedAt,
 *   }
 * ]
 */
export const sortByCreatedAt = <T extends { createdAt?: string | Date }>(
  rowA: { original: T },
  rowB: { original: T }
): number => {
  const dateA = rowA.original.createdAt;
  const dateB = rowB.original.createdAt;

  if (!dateA || !dateB) return 0;

  const createdAtA = dateA instanceof Date ? dateA : new Date(dateA);
  const createdAtB = dateB instanceof Date ? dateB : new Date(dateB);

  if (createdAtA < createdAtB) return -1;
  if (createdAtA > createdAtB) return 1;
  return 0;
};

/**
 * Sort function for rows with an updatedAt date field
 */
export const sortByUpdatedAt = <T extends { updatedAt?: string | Date }>(
  rowA: { original: T },
  rowB: { original: T }
): number => {
  const dateA = rowA.original.updatedAt;
  const dateB = rowB.original.updatedAt;

  if (!dateA || !dateB) return 0;

  const updatedAtA = dateA instanceof Date ? dateA : new Date(dateA);
  const updatedAtB = dateB instanceof Date ? dateB : new Date(dateB);

  if (updatedAtA < updatedAtB) return -1;
  if (updatedAtA > updatedAtB) return 1;
  return 0;
};

/**
 * Sort function for generic date fields
 */
export const sortByDate = <T>(
  rowA: { original: T },
  rowB: { original: T },
  columnId: keyof T
): number => {
  const dateA = rowA.original[columnId];
  const dateB = rowB.original[columnId];

  if (!dateA || !dateB) return 0;

  const parsedA =
    dateA instanceof Date ? dateA : new Date(dateA as string | number);
  const parsedB =
    dateB instanceof Date ? dateB : new Date(dateB as string | number);

  if (parsedA < parsedB) return -1;
  if (parsedA > parsedB) return 1;
  return 0;
};

/**
 * Sort function for numeric fields (handles string numbers)
 */
export const sortByNumber = <T>(
  rowA: { original: T },
  rowB: { original: T },
  columnId: keyof T
): number => {
  const valA = rowA.original[columnId];
  const valB = rowB.original[columnId];

  const numA = typeof valA === "number" ? valA : Number.parseFloat(String(valA));
  const numB = typeof valB === "number" ? valB : Number.parseFloat(String(valB));

  if (Number.isNaN(numA) && Number.isNaN(numB)) return 0;
  if (Number.isNaN(numA)) return 1;
  if (Number.isNaN(numB)) return -1;

  return numA - numB;
};

/**
 * Case-insensitive string sort
 */
export const sortByString = <T>(
  rowA: { original: T },
  rowB: { original: T },
  columnId: keyof T
): number => {
  const valA = String(rowA.original[columnId] ?? "").toLowerCase();
  const valB = String(rowB.original[columnId] ?? "").toLowerCase();

  return valA.localeCompare(valB);
};
